// Sprint UI-9 — toast store bridged to sonner.
//
// The push() / dismiss() / clear() API stays unchanged so all existing
// callers (Sidebar, ChatView, PermissionCard, useUndoToast, useDeepLink,
// pwa-update) continue to work. Internally, push() now calls sonner's
// toast() function, which handles rendering and auto-dismiss.

import { create } from 'zustand';
import { toast as sonnerToast } from 'sonner';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  action?: ToastAction;
  durationMs: number;
  createdAt: number;
}

interface ToastStoreState {
  toasts: Toast[];
  push: (input: {
    message: string;
    action?: ToastAction;
    durationMs?: number;
  }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const DEFAULT_DURATION_MS = 5_000;

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const useToastStore = create<ToastStoreState>((set) => ({
  toasts: [],
  push: ({ message, action, durationMs = DEFAULT_DURATION_MS }) => {
    const id = randomId();
    const toastRecord: Toast = {
      id,
      message,
      action,
      durationMs,
      createdAt: Date.now(),
    };

    set((s) => ({ toasts: [...s.toasts, toastRecord] }));

    sonnerToast(message, {
      id,
      duration: durationMs === 0 ? Infinity : durationMs,
      action: action
        ? { label: action.label, onClick: action.onClick }
        : undefined,
      onDismiss: () => {
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
      },
      onAutoClose: () => {
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
      },
    });

    return id;
  },
  dismiss: (id) => {
    sonnerToast.dismiss(id);
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  clear: () => {
    sonnerToast.dismiss();
    set({ toasts: [] });
  },
}));
